import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, AlertCircle, AlertTriangle, Info, PoundSterling } from 'lucide-react';
import { ValidationIssue } from '@/types/payroll-validator';

interface IssueDetailCardProps {
  issue: ValidationIssue;
}

export const IssueDetailCard: React.FC<IssueDetailCardProps> = ({ issue }) => {
  const [expanded, setExpanded] = useState(false);

  const getSeverityConfig = (severity: string) => {
    switch (severity) {
      case 'critical':
        return {
          color: 'bg-red-100 text-red-800 border-red-300',
          icon: <AlertCircle className="h-4 w-4" />,
          label: 'Critical',
        };
      case 'warning':
        return {
          color: 'bg-orange-100 text-orange-800 border-orange-300',
          icon: <AlertTriangle className="h-4 w-4" />,
          label: 'Warning',
        };
      default:
        return {
          color: 'bg-blue-100 text-blue-800 border-blue-300',
          icon: <Info className="h-4 w-4" />,
          label: 'Info',
        };
    }
  };

  const severityConfig = getSeverityConfig(issue.severity);
  const isPayIdIssue = issue.rule_code === 'PAY_ID_DUPLICATE' || issue.rule_code === 'PAY_ID_MISSING';

  // Helper to format markdown-like content to HTML
  const formatContent = (content: string) => {
    if (!content) return content;
    
    // Convert markdown bullet points to HTML
    let formatted = content.replace(/^- (.+)$/gm, '<li>$1</li>');
    if (formatted.includes('<li>')) {
      formatted = `<ul class="list-disc pl-5 space-y-1">${formatted}</ul>`;
    }
    
    // Convert **bold** to <strong>
    formatted = formatted.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
    
    // Convert line breaks
    formatted = formatted.replace(/\n\n/g, '</p><p class="mt-2">');
    formatted = `<p>${formatted}</p>`;
    
    return formatted;
  };

  return (
    <Card className={`mb-3 hover:shadow-md transition-shadow ${isPayIdIssue ? 'border-2 border-red-500' : ''}`}>
      <CardContent className="p-4">
        <div
          className="flex items-center justify-between cursor-pointer"
          onClick={() => setExpanded(!expanded)}
        >
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Severity Badge */}
            <Badge className={`${severityConfig.color} flex items-center gap-1 flex-shrink-0`}>
              {severityConfig.icon}
              {severityConfig.label}
            </Badge>

            {/* Title */}
            <h3 className="font-semibold text-gray-900 truncate">
              {issue.title}
            </h3>

            {/* Pay ID Issue Warning Badge */}
            {isPayIdIssue && (
              <Badge className="bg-red-600 text-white flex-shrink-0">
                DOUBLE PAYMENT
              </Badge>
            )}

            {/* Cost Badge */}
            {issue.estimated_cost_impact && issue.estimated_cost_impact > 0 && (
              <Badge variant="outline" className="ml-auto flex-shrink-0 text-yellow-700 border-yellow-300 bg-yellow-50">
                <PoundSterling className="h-3 w-3 mr-1" />
                {issue.estimated_cost_impact.toLocaleString('en-GB', {
                  minimumFractionDigits: 0,
                  maximumFractionDigits: 0,
                })}
              </Badge>
            )}
          </div>

          {/* Expand Icon */}
          <ChevronDown
            className={`h-5 w-5 text-gray-400 flex-shrink-0 ml-2 transition-transform duration-200 ${
              expanded ? 'rotate-180' : ''
            }`}
          />
        </div>

        {expanded && (
          <div className="mt-4 space-y-4">
            {/* Description */}
            <div>
              <h4 className="font-medium text-sm text-gray-700 mb-1">Issue Details</h4>
              <p className="text-gray-800">{issue.description}</p>
            </div>

            {/* Affected Employee */}
            {issue.employee && (
              <div className="text-sm bg-gray-50 p-3 rounded-md">
                <span className="font-medium text-gray-700">Affected Employee: </span>
                <span className="text-gray-900">
                  {issue.employee.first_name} {issue.employee.last_name}
                </span>
                {issue.employee.external_employee_id && (
                  <span className="text-gray-600 ml-2">
                    (ID: {issue.employee.external_employee_id})
                  </span>
                )}
              </div>
            )}

            {/* Why It Matters */}
            {issue.why_it_matters && (
              <div className="bg-blue-50 border border-blue-200 p-4 rounded-md">
                <h4 className="font-semibold text-sm text-blue-900 mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Why It Matters
                </h4>
                <div
                  className="text-blue-800 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formatContent(issue.why_it_matters) }}
                />
              </div>
            )}

            {/* Consequence Simulation */}
            {issue.consequence_simulation && (
              <div className="bg-red-50 border border-red-200 p-4 rounded-md">
                <h4 className="font-semibold text-sm text-red-900 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  What Could Happen
                </h4>
                <div
                  className="text-red-800 text-sm leading-relaxed"
                  dangerouslySetInnerHTML={{ __html: formatContent(issue.consequence_simulation) }}
                />
              </div>
            )}

            {/* Cost Impact */}
            {issue.estimated_cost_impact && issue.estimated_cost_impact > 0 && (
              <div className={`p-4 rounded-md ${isPayIdIssue ? 'bg-red-50 border-2 border-red-300' : 'bg-yellow-50 border border-yellow-200'}`}>
                <h4 className={`font-semibold text-sm mb-2 flex items-center gap-2 ${isPayIdIssue ? 'text-red-900' : 'text-yellow-900'}`}>
                  <PoundSterling className="h-4 w-4" />
                  Financial Impact {isPayIdIssue && '(DOUBLED)'}
                </h4>
                <p className={`text-3xl font-bold mb-2 ${isPayIdIssue ? 'text-red-900' : 'text-yellow-900'}`}>
                  £{issue.estimated_cost_impact.toLocaleString('en-GB', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                {issue.cost_impact_explanation && (
                  <div
                    className={`text-sm leading-relaxed whitespace-pre-line ${isPayIdIssue ? 'text-red-800' : 'text-yellow-800'}`}
                    dangerouslySetInnerHTML={{ __html: formatContent(issue.cost_impact_explanation) }}
                  />
                )}
              </div>
            )}

            {/* How to Fix */}
            {issue.how_to_fix && (
              <div className="bg-green-50 border border-green-200 p-4 rounded-md">
                <h4 className="font-semibold text-sm text-green-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="h-4 w-4" />
                  How to Fix
                </h4>
                <div
                  className="text-green-800 text-sm leading-relaxed prose prose-sm max-w-none"
                  dangerouslySetInnerHTML={{ __html: formatContent(issue.how_to_fix) }}
                />
              </div>
            )}

            {/* Technical Details (Collapsible) */}
            {(issue.expected_values || issue.actual_values) && (
              <details className="text-sm">
                <summary className="cursor-pointer font-medium text-gray-700 hover:text-gray-900">
                  Technical Details
                </summary>
                <div className="mt-2 space-y-2 bg-gray-50 p-3 rounded-md">
                  {issue.expected_values && (
                    <div>
                      <span className="font-medium text-gray-700">Expected: </span>
                      <code className="text-xs bg-white px-2 py-1 rounded border">
                        {JSON.stringify(issue.expected_values, null, 2)}
                      </code>
                    </div>
                  )}
                  {issue.actual_values && (
                    <div>
                      <span className="font-medium text-gray-700">Actual: </span>
                      <code className="text-xs bg-white px-2 py-1 rounded border">
                        {JSON.stringify(issue.actual_values, null, 2)}
                      </code>
                    </div>
                  )}
                  <div>
                    <span className="font-medium text-gray-700">Confidence: </span>
                    <span className="text-gray-900">
                      {(issue.confidence * 100).toFixed(0)}%
                    </span>
                  </div>
                </div>
              </details>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

